import {
  Anchor,
  Button,
  Card,
  H1,
  H2,
  H3,
  H4,
  H5,
  Paragraph,
  Separator,
  Spinner,
  XStack,
  YStack,
  ChipsWithIconControlled,
  Accordion,
  Tabs,
  SizableText,
  SimpleText,
  useMedia,
  ActiveButton,
  TZATabs,
  ScrollView
} from '@my/ui'
import type { TabsContentProps } from 'tamagui'
import { useState } from 'react';
import { useAuthedRequestFn, useLink, TZABrandGuidelines, color, OpenExternalLink } from '@my/util';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { ZAInvestmentPortfolioMarkdown, ZAInvestmentPortfolioDefinitions, ZAInvestmentPortfolioDefinitions2026 } from '../ZAInvestmentPortfolioMarkdown';
import { FinancialReviewHeading, HomeMainHeading } from '../../../components';
import assets from '../../../assets/images'
import { AccordionTrigger } from '../../financial-profile/helpers/Helper';
import { FinancialAccountsTable } from '../../financial-profile/form-sections/LinkedFinancialAccounts';
import { Target, Check } from '@tamagui/lucide-icons-2';
import { FinancialProfileButton } from '../../home/helper';
import { PortfolioDetailView } from './PortfolioDetailView';
import { Platform } from 'react-native';

const TabsContent = (props: TabsContentProps) => {
  return (
    <Tabs.Content
      backgroundColor="$background"
      key="tab3"
      paddingTop="$2"
      /* alignItems="center"
      justifyContent="center" */
      //flex={1}
      borderColor="$background"
      borderRadius="$2"
      borderTopLeftRadius={0}
      borderTopRightRadius={0}
      borderWidth="$2"
      {...props}
    >
      {props.children}
    </Tabs.Content>
  )
}

export const PORTFOLIO_NAME_MAPPING: Record<string, string> = {
  "ZA Long-Term Growth": "ZA Long-Term Growth Portfolio",
  "ZA Long-Term Conservative/Defensive": "ZA Long-Term Defensive Portfolio",
  "ZA Climate Transition (LT)": "ZA Climate Transition Portfolio",
  "ZA Long-Term Low Min": "ZA Long-Term Low Minimum Portfolio",
  "ZA Short-Term Growth": "ZA Short-Term Equity Portfolio",
  "ZA Short-Term Conservative": "ZA Short-Term Defensive Portfolio",
  "ZA Short-Term Low Min": "ZA Short-Term Low Minimum Portfolio",
};

/**
 * Helper to get the correct JSON key from legacy/short names.
 * Returns null if no match found.
 */
export const getPortfolioKey = (oldName: string): keyof typeof ZAInvestmentPortfolioDefinitions2026.portfolios | null => {
  return PORTFOLIO_NAME_MAPPING[oldName] as any || null;
};


const JsonRender: React.FC<{
  portfolio: keyof typeof ZAInvestmentPortfolioDefinitions
  investNow: () => void
  investments?: {
    readyToInvest?: boolean;
  };
  investmentsIsLoading?: boolean;
}> = ({portfolio, investNow, investments, investmentsIsLoading}) => {

  const media = useMedia()
  //const definition = ZAInvestmentPortfolioDefinitions[portfolio];
  const portfolioName = getPortfolioKey(portfolio) || '';
  const updatedDefinition = portfolioName ? ZAInvestmentPortfolioDefinitions2026.portfolios?.[portfolioName] : undefined;

  const IBKRButton = (linkText = 'Invest Now') => <ActiveButton
    onPress={investNow}
    ButtonTextProps={{children: linkText}}
  />

  const investNowSection = investmentsIsLoading
  ? <Spinner size="large" color="$green10" />
  : investments?.readyToInvest
  ? <>
      <YStack backgroundColor={color.dullGrey} padding={'$4'} borderRadius={10}>
        <XStack mb={'$2'}>
          <SimpleText fontWeight={'700'} fontSize={18} >
            Notice:
          </SimpleText>
        </XStack>
        <SimpleText fontSize={16}>
          You have elected to invest in the <SimpleText fontWeight={'700'}>{portfolioName}</SimpleText> portfolio. Click the ZA Investment Portal button below to view your custodian account (or complete your application, if still in progress.)
        </SimpleText>
      </YStack>
      {IBKRButton('ZA Investment Portal')}
    </>
  : IBKRButton()

  return !updatedDefinition ? null : <YStack>
    <YStack padding={10}>
      {media['max-sm'] ? <H3>{portfolioName}</H3> : <H2>{portfolioName}</H2>}
      <XStack><ChipsWithIconControlled text="Matched to You" color={color.tzaGreen} /></XStack>
    </YStack>
    <Tabs
      defaultValue="tab1"
      orientation="horizontal"
      flexDirection="column"
      //$maxMd={{ width: 300 }}
      width={'100%'}
      //height={150}
      borderRadius="$4"
      borderWidth="$0.25"
      overflow="hidden"
      borderColor="$borderColor"
    >
      <TZATabs.List
        separator={<Separator vertical />}
        //disablePassBorderRadius="bottom"
        aria-label="Manage your account"
      >
        <TZATabs.Tab
          value="tab1"
        >
          <SizableText fontFamily="$body" textAlign="center">
            Overview
          </SizableText>
        </TZATabs.Tab>
        <TZATabs.Tab
          value="tab2"
        >
          <SizableText fontFamily="$body" textAlign="center">
            FAQs
          </SizableText>
        </TZATabs.Tab>
      </TZATabs.List>
      <Separator />
      <TabsContent value="tab1">
        <YStack gap={20}>
          <Card elevate size="$4" bordered padding={18} paddingHorizontal={media['max-sm'] ? 0 : undefined} background={'white'}>
            <YStack>
              <Paragraph fontSize={16}>{updatedDefinition?.overview}</Paragraph>
            </YStack>
          </Card>
          {investNowSection}
          
          <PortfolioDetailView portfolioName={portfolioName || ''} />
        </YStack>
      </TabsContent>

      <TabsContent value="tab2">
        {/* <H5>Connections</H5> */}
        <YStack gap={20}>
          <Card elevate size="$4" bordered background={'white'} style={{width: '100%'}}>
            <Card.Header paddingHorizontal={media['max-sm'] ? 0 : undefined}><H4>Why We Matched You Here</H4></Card.Header>
            <YStack style={{padding: `0px ${media['max-sm'] ? 0 : 18}px 18px`}} gap={10} >
              {Object.entries(updatedDefinition['Why We Chose This for You']).map(([title, text], idx) => <XStack key={idx} gap={10} style={{width: '100%'}}>
                <YStack>
                  <XStack style={{
                    marginTop: 5, width: 30, height: 30, 
                    backgroundColor: TZABrandGuidelines.colorsMap[idx === 0 ? 'tertiaryCornSilk' : 'tertiaryNonPhotoBlue'], 
                    borderRadius: 30, alignItems: 'center', justifyContent: 'center' 
                  }}>
                    {idx === 0 ? <Target color={TZABrandGuidelines.colorsMap.secondaryMustard} /> : <Check color={TZABrandGuidelines.colorsMap.secondaryBlue} /> }
                  </XStack>
                </YStack>
                <YStack style={{flexShrink: 1}}>
                  <H5 fontSize="$5" fontWeight={700}>{title}</H5>
                  <Paragraph style={{textWrap: 'wrap'}}>{text}</Paragraph>
                </YStack>
              </XStack>)}
            </YStack>
          </Card>
          <Card elevate size="$4" bordered background={'white'}>
            <Card.Header paddingHorizontal={media['max-sm'] ? 0 : undefined}><H4>Frequently Asked Questions</H4></Card.Header>
            <YStack style={{padding: `0px ${media['max-sm'] ? 0 : 18}px 18px`}} gap={10}>
              {Object.entries(updatedDefinition['FAQs']).map(([title, text], idx) => <XStack key={idx}>
                <YStack width={'95%'}>
                  <H5 fontSize="$5" fontWeight={700}>{title}</H5>
                  <Paragraph>{text}</Paragraph>
                </YStack>
              </XStack>)}
            </YStack>
          </Card>
        </YStack>
      </TabsContent>
    </Tabs>
  </YStack>
}

export function FinancialInvestmentsScreen() {
  const queryClient = useQueryClient()
  //const link = useLink('/')
  const authedRequestFn = useAuthedRequestFn();
  const reviewDataQuery = useQuery({ 
    queryKey: ['financial-review'], 
    queryFn: () => authedRequestFn('/financial-review')
  });
  const financialProfileQuery = useQuery({
    queryKey: ['financial-profile'],
    queryFn: () => authedRequestFn('/financial-profile'),
  });
  const userDataQuery = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => authedRequestFn('/user-profile'),
  });

  const investMutation = useMutation({
    mutationFn: () =>
      authedRequestFn('/invest-now', { method: 'GET' }),
    //onMutate: () => setFormSubmitting('save'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] })
      //toast.show('Successfully saved!', { message: 'Your data has been saved.' })
    },
    //onSettled: () => setFormSubmitting(false),
    onError: (error) => {
      console.log(error)
    },
  })

  const media = useMedia();

  const AccordionContentProps = {
    style: { backgroundColor: 'white' },
    padding: media['max-sm'] ? '18px 0px' : 18
  }
  const plannedInvestmentAmount = financialProfileQuery.data?.['Risk & Investment Prefs - Planned Investment Amount'];
  const isInvestingLessThan5k = [null, undefined, 'Less than $5,000'].includes(plannedInvestmentAmount);
  const ibkrAccountId = userDataQuery.data?.['investments']?.ibkrAccountId;

  const investNow = () => {
    const youngProfessionalTemplate = 'https://www.clientam.com/Universal/servlet/formWelcome?partnerID=parity_za&invitation_id=176210902&token=11904&invitedBy=bmdxZHdxMTcw';
    const lowCostTemplate = 'https://www.clientam.com/Universal/servlet/formWelcome?partnerID=parity_za&invitation_id=176210891&token=76628&invitedBy=bmdxZHdxMTcw';
    const clientPortal = 'https://www.clientam.com/sso/Login?partnerID=parity_za';
    const IBKR_Template_Link = ibkrAccountId
    ? clientPortal
    : isInvestingLessThan5k ? lowCostTemplate : youngProfessionalTemplate;

    const openIBKR = () => OpenExternalLink(IBKR_Template_Link);
    //openIBKR();
    investMutation.mutate(undefined, {onSuccess: openIBKR});
  }

  const dataLoading = (reviewDataQuery.isLoading || financialProfileQuery.isLoading || userDataQuery.isLoading)// && financialProfileQuery.data;
  const review = (reviewDataQuery.data && userDataQuery.data)
  ? <JsonRender
    portfolio={reviewDataQuery.data['zaInvestmentPortfolio'] as any}
    investNow={investNow}
    investments={userDataQuery.data?.['investments']}
    investmentsIsLoading={userDataQuery.isLoading}
  />
  : null;

  const linkedFinancialData = financialProfileQuery?.data?.['Linked Financial Data'] || {}
  const investments = (financialProfileQuery.data)
  ? <FinancialAccountsTable linkedFinancialData={linkedFinancialData} type={'investment'} />
  : null;

  const financialProfileExists = financialProfileQuery?.data && reviewDataQuery.data?.['zaInvestmentPortfolio']
  const emergencyFundExists = !!financialProfileQuery?.data?.["Assets - Emergency Fund - Exists"];
  const emergencyFundHasEnoughSavings = !!financialProfileQuery?.data?.["Assets - Emergency Fund - Has 3-6 Months Savings"];
  const hasNoHighInterestDebt = financialProfileQuery?.data?.['Debt - Has High Interest'] === false;
  const meetsInvestmentCriteria = emergencyFundExists && emergencyFundHasEnoughSavings && hasNoHighInterestDebt;
  const investWithUsSection = meetsInvestmentCriteria
  ? review
  : <YStack gap={20}>
    <YStack backgroundColor={color.dullGrey} padding={'$4'} borderRadius={10}>
      <XStack mb={'$2'}>
        <SimpleText fontWeight={'700'} fontSize={18} color={color.grey}>
          Notice:
        </SimpleText>
      </XStack>
      <SimpleText fontSize={16} color={color.grey}>
        In order to Invest in The Zillennial Advisors curated model portfolios, you must first establish an Emergency Fund containing 3-6 months of savings and eliminate any high interest debt (5-6%). Your current Financial Profile does not meet this criteria. If this is incorrect, make sure to update your Financial Profile:
      </SimpleText>
    </YStack>
    <FinancialProfileButton buttonText='Update your Financial Profile' />
  </YStack>

  const mainContent = dataLoading 
  ? <Spinner size="large" color={TZABrandGuidelines.colorsMap.primaryGreen} /> 
  : <Accordion overflow="hidden" type="multiple" defaultValue={['a1', 'a2']}>
    <Accordion.Item value="a1">
      <AccordionTrigger title={'Invest with Us'} />
      <Accordion.HeightAnimator transition="medium">
        <Accordion.Content transition="medium" exitStyle={{ opacity: 0 }} {...AccordionContentProps}>
          {investWithUsSection}
        </Accordion.Content>
      </Accordion.HeightAnimator>
    </Accordion.Item>
    <Accordion.Item value="a2">
      <AccordionTrigger title={'Investment Accounts'} />
      <Accordion.HeightAnimator transition="medium">
        <Accordion.Content transition="medium" exitStyle={{ opacity: 0 }} {...AccordionContentProps}>
          {investments}
        </Accordion.Content>
      </Accordion.HeightAnimator>
    </Accordion.Item>
  </Accordion>

  const condDisplay = financialProfileExists
  ? mainContent
  : <>
    <FinancialProfileButton buttonText='Please create your Financial Profile' />
  </>
  
  const output = (

    <YStack f={1} bc="$background" p="$4" gap={20}>
      <FinancialReviewHeading
        title="Investments"
        img={assets.investWithUs}
        imageStyle={{
          //'$platform-native': { width: 26, height: 22 },
          //'$platform-web': { width: 24, height: 26 },
          resizeMode: 'contain',
        }}
      />
      {condDisplay}
    </YStack>
  );
  
  return (
    
      Platform.OS === 'web' ? (
        output
      ) : (
        <ScrollView backgroundColor={color.white}>
          {output}
        </ScrollView>
      )
    
  )
}
